import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Download, Loader2, Pencil } from "lucide-react";
import { G, ROW_LABEL, ROW_TONE, download, errMsg } from "./guestApi";
import { RowEditor } from "./RowEditor";

function ConflictCell({ r, resolve }) {
  return (
    <div className="space-y-1">
      {r.conflicts.map((c) => (
        <div key={c.field} className="text-[11px]"><strong>{c.field}</strong>: saved “{c.existing}” · file “{c.incoming}”</div>))}
      <div className="flex gap-1 flex-wrap">
        {[["KEEP_EXISTING", "Keep saved"], ["USE_INCOMING", "Use file"], ["SKIP", "Skip"]].map(([v, l]) => (
          <Button key={v} size="sm" className="h-6 px-2 text-[11px]" variant={r.resolution === v ? "default" : "outline"}
            onClick={() => resolve(r, v)} data-testid={`guest-resolve-${v}-${r.id}`}>{l}</Button>))}
      </div>
    </div>
  );
}

function CommitBar({ batch, summary, session, onDone }) {
  const [skip, setSkip] = useState(false);
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${G}/import/commit`, { token: session.token, batch_id: batch.id,
        skip_unresolved: skip, consent_evidence: evidence || null });
      toast.success("Import saved"); onDone(r.data.result);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t pt-3 space-y-2" data-testid="guest-commit-bar">
      <div className="text-xs">
        <label className="font-medium">Opt-in evidence (optional)</label>
        <Input className="h-8 mt-1" value={evidence} onChange={(e) => setEvidence(e.target.value)}
          placeholder="Only if the file's YES/opt-in values are backed by real consent, e.g. 'Signed feedback forms, May 2026'"
          data-testid="guest-commit-evidence" />
        <div className="text-slate-500 mt-1">Without evidence, opt-in values stay Unknown. Opt-outs (NO / STOP) are always applied, and existing opt-outs are never overwritten.</div>
      </div>
      {summary.unresolved > 0 && (
        <label className="flex items-center gap-2 text-xs text-amber-900">
          <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} data-testid="guest-commit-skip-unresolved" />
          Skip the {summary.unresolved} unreviewed row(s) and import the rest
        </label>)}
      <Button onClick={commit} disabled={busy || (summary.unresolved > 0 && !skip)} data-testid="guest-commit-btn">
        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save guests
      </Button>
    </div>
  );
}

export function ImportReview({ session, batchId, onClose }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.post(`${G}/import/rows`, { token: session.token, batch_id: batchId,
        status: filter || null, page_size: 500 });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [session.token, batchId, filter]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (r, resolution) => {
    try {
      await api.post(`${G}/import/row-update`, { token: session.token, batch_id: batchId, row_id: r.id, resolution });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <div className="text-sm text-slate-500 p-4"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>;
  const { batch, summary, rows } = data;
  const locked = batch.status !== "REVIEW";
  const report = () => download(`${G}/import/error-report`, { token: session.token, batch_id: batchId },
    `import_issues_${batch.center}.xlsx`).catch((e) => toast.error(errMsg(e)));
  const cancel = async () => { await api.post(`${G}/import/cancel`, { token: session.token, batch_id: batchId }); onClose(); };

  return (
    <Card data-testid="guest-import-review">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm">Review · {batch.file_name} · {batch.center}
          <Badge className="ml-2" variant="outline" data-testid="guest-review-batch-status">{batch.status}</Badge></CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={report} data-testid="guest-error-report-btn">
            <Download className="w-4 h-4 mr-1" />Issues report</Button>
          {!locked && <Button size="sm" variant="ghost" onClick={cancel} data-testid="guest-import-cancel">Cancel import</Button>}
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="guest-review-close">Close</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1" data-testid="guest-review-summary">
          <Button size="sm" variant={!filter ? "default" : "outline"} className="h-7" onClick={() => setFilter("")}>All {summary.total}</Button>
          {summary.unresolved > 0 && <Button size="sm" className="h-7" variant={filter === "UNRESOLVED" ? "default" : "outline"}
            onClick={() => setFilter("UNRESOLVED")} data-testid="guest-filter-unresolved">Needs review {summary.unresolved}</Button>}
          {Object.entries(summary.counts).map(([k, n]) => (
            <Button key={k} size="sm" className="h-7" variant={filter === k ? "default" : "outline"}
              onClick={() => setFilter(k)} data-testid={`guest-filter-${k}`}>{ROW_LABEL[k] || k} {n}</Button>))}
        </div>
        {batch.result && (
          <div className="rounded border border-emerald-300 bg-emerald-50 p-2 text-xs flex gap-2" data-testid="guest-commit-result">
            <CheckCircle2 className="w-4 h-4 text-emerald-700" />
            {Object.entries(batch.result.counts).map(([k, n]) => `${k}: ${n}`).join(" · ")}
          </div>)}
        <div className="border rounded overflow-auto max-h-[55vh]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 text-left"><tr>
              <th className="p-1.5">Where</th><th className="p-1.5">Name</th><th className="p-1.5">Phone</th>
              <th className="p-1.5">Status</th><th className="p-1.5">Details</th><th className="p-1.5" /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t align-top" data-testid={`guest-review-row-${r.id}`}>
                  <td className="p-1.5 text-slate-500 whitespace-nowrap">{r.ref.sheet} · {r.ref.table_id.split("::")[1] || ""} · r{r.ref.row_no}</td>
                  <td className="p-1.5">{r.raw.name}</td>
                  <td className="p-1.5 font-mono">{r.raw.phone}{r.phone?.e164 && r.phone.e164 !== r.raw.phone &&
                    <div className="text-emerald-700">→ {r.phone.e164}</div>}</td>
                  <td className="p-1.5"><Badge className={ROW_TONE[r.status]}>{ROW_LABEL[r.status] || r.status}</Badge>
                    {r.resolution && <div className="text-[10px] mt-0.5">✓ {r.resolution}</div>}
                    {r.commit_outcome && <div className="text-[10px] mt-0.5 text-slate-500">{r.commit_outcome}</div>}</td>
                  <td className="p-1.5">
                    {r.status === "CONFLICT" && !locked ? <ConflictCell r={r} resolve={resolve} /> :
                      <span className="text-slate-600">{(r.issues || []).join("; ")}</span>}
                  </td>
                  <td className="p-1.5">{!locked && (
                    <Button size="sm" variant="ghost" className="h-6" onClick={() => setEditing(r)}
                      data-testid={`guest-review-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>)}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
        {!locked && <CommitBar batch={batch} summary={summary} session={session} onDone={load} />}
      </CardContent>
      {editing && <RowEditor row={editing} session={session} batchId={batchId}
        onClose={() => setEditing(null)} onSaved={load} />}
    </Card>
  );
}
